/**
 * NoticePopup — the notification pop-up in Studio (Design's sketch, Nino OK
 * 11 Oct: "Same card in Studio and Workspace"). It is the learner web app's
 * card (playbook-web NotificationPopup, 32f41e1) on Studio's notices.
 *
 * One small card: top right under the top bar on a computer (380 px), full
 * width less 8 px on phones. Fixed slots, so every moment is the same height:
 * animation tile, group label, title (1 line), message (2 lines), one button,
 * ✕. Stays 6 s with a timer line that pauses under the pointer or a finger;
 * urgent ones ("learner waiting") stay until tapped, with a blue border and
 * "Live". Tapping the card does what its button does. Reduced motion: a
 * 150 ms fade showing the final picture.
 *
 * Words come from the server in the facilitator's language; only the group
 * label, "Live", the folded card and ✕ are Studio's.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '@/lib/i18n';
import type { Notice, NoticeKind, PopupStep } from '@/lib/notices';
import './NoticePopup.css';

const SHOW_MS = 6000;
const PILLARS = ['#FF0080', '#00FFFF', '#0080FF', '#80FF00', '#FFFF00'];
const CREAM = '#FAF0E6';

type Anim = 'rings' | 'tick' | 'clock' | 'live' | 'info' | 'back' | 'stack';

/* Each facilitator type's animation (Design's sketch); others take their feeling's. */
const BY_TYPE: Record<string, Anim> = {
  scores_to_review: 'rings',
  fac_session_confirmed: 'tick',
  fac_reminder_24h: 'clock',
  fac_reminder_15min: 'clock',
  learner_waiting: 'live',
  fac_session_cancelled: 'back',
};
const BY_KIND: Record<NoticeKind, Anim> = { celebrate: 'rings', get_ready: 'tick', heads_up: 'info', invite: 'info' };

const animFor = (n: Notice): Anim => BY_TYPE[n.type] ?? BY_KIND[n.kind] ?? 'info';

const ROT = { transform: 'rotate(-90deg)', transformOrigin: 'center' } as const;

function burst(colors: string[], cx: number, cy: number, r: number, dist: number, delay: number): ReactNode {
  return (
    <g className="npop-burst">
      {colors.map((c, i) => {
        const a = (i / colors.length) * Math.PI * 2 - Math.PI / 2;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill={c}
            style={{ ['--dx' as string]: `${Math.round(Math.cos(a) * dist)}px`, ['--dy' as string]: `${Math.round(Math.sin(a) * dist)}px`, animationDelay: `${delay}s` }} />
        );
      })}
    </g>
  );
}

function Tile({ anim, count }: { anim: Anim; count?: number }) {
  return (
    <svg viewBox="0 0 56 56" className={'npop-a' + (anim === 'info' || anim === 'back' ? ' npop-once' : '')} aria-hidden>
      {anim === 'rings' && (
        <>
          {PILLARS.slice(0, 3).map((c, i) => (
            <circle key={i} cx="28" cy="28" r={22 - i * 6} fill="none" stroke={c} strokeWidth="3.5" strokeLinecap="round" pathLength={1}
              className="npop-draw" style={{ ...ROT, animationDelay: `${i * 0.12}s` }} />
          ))}
          {burst(PILLARS, 28, 28, 3, 26, 0.75)}
        </>
      )}
      {anim === 'tick' && (
        <>
          <circle cx="28" cy="28" r="20" fill="none" stroke={CREAM} strokeWidth="2.6" pathLength={1} className="npop-draw" style={ROT} />
          <path d="M19 29l6 6 12-13" fill="none" stroke={CREAM} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" pathLength={1}
            className="npop-draw" style={{ animationDelay: '.6s', animationDuration: '.5s' }} />
        </>
      )}
      {anim === 'clock' && (
        <>
          <circle cx="28" cy="28" r="20" fill="none" stroke={CREAM} strokeWidth="2.6" pathLength={1} className="npop-draw" style={{ ...ROT, animationDuration: '.6s' }} />
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={28 + Math.cos((i * Math.PI) / 2) * 15} cy={28 + Math.sin((i * Math.PI) / 2) * 15} r="1.3" fill="rgba(250,240,230,.5)" />
          ))}
          <line x1="28" y1="28" x2="28" y2="18" stroke={CREAM} strokeWidth="2.6" strokeLinecap="round" />
          <line x1="28" y1="28" x2="36" y2="32" stroke="rgba(250,240,230,.6)" strokeWidth="2.6" strokeLinecap="round" className="npop-hand" />
          <circle cx="28" cy="28" r="2.4" fill={CREAM} />
        </>
      )}
      {anim === 'live' && (
        <>
          <circle cx="28" cy="28" r="12" fill="none" stroke="#FF0080" strokeWidth="2" className="npop-ripple" />
          <circle cx="28" cy="28" r="12" fill="none" stroke="#FF0080" strokeWidth="2" className="npop-ripple" style={{ animationDelay: '.8s' }} />
          <circle cx="28" cy="28" r="9" fill="#FF0080" className="npop-pop" />
          <path d="M25 23.5v9l7.5-4.5z" fill="#fff" className="npop-pop" style={{ animationDelay: '.15s' }} />
        </>
      )}
      {anim === 'info' && (
        <>
          <circle cx="28" cy="28" r="20" fill="none" stroke={CREAM} strokeWidth="2.4" className="npop-ripple" style={{ animationDelay: '.7s' }} />
          <circle cx="28" cy="28" r="20" fill="none" stroke={CREAM} strokeWidth="2.6" pathLength={1} className="npop-draw" style={{ ...ROT, animationDuration: '.7s' }} />
          <circle cx="28" cy="20" r="2" fill={CREAM} className="npop-pop" style={{ animationDelay: '.6s' }} />
          <line x1="28" y1="26" x2="28" y2="37" stroke={CREAM} strokeWidth="3" strokeLinecap="round" className="npop-pop" style={{ animationDelay: '.7s' }} />
        </>
      )}
      {anim === 'back' && (
        <>
          <circle cx="28" cy="28" r="20" fill="none" stroke={CREAM} strokeWidth="2.4" className="npop-ripple" style={{ animationDelay: '.9s' }} />
          <path d="M40 22a14 14 0 1 0 2 10" fill="none" stroke={CREAM} strokeWidth="2.6" strokeLinecap="round" pathLength={1} className="npop-draw" style={{ animationDuration: '.8s' }} />
          <path d="M41 15v8h-8" fill="none" stroke={CREAM} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="npop-pop" style={{ animationDelay: '.7s' }} />
        </>
      )}
      {anim === 'stack' && (
        <>
          <rect x="14" y="10" width="28" height="22" rx="5" fill="#16171b" stroke="rgba(250,240,230,.3)" strokeWidth="1.6" className="npop-pop" style={{ animationDelay: '.3s' }} />
          <rect x="11" y="16" width="34" height="24" rx="5" fill="#16171b" stroke="rgba(250,240,230,.45)" strokeWidth="1.6" className="npop-pop" style={{ animationDelay: '.15s' }} />
          <rect x="8" y="22" width="40" height="26" rx="6" fill="#1e2026" stroke={CREAM} strokeWidth="1.8" className="npop-pop" />
          <text x="28" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill={CREAM} className="npop-pop">{count}</text>
        </>
      )}
    </svg>
  );
}

const GROUP_KEY: Record<NoticeKind, string> = {
  celebrate: 'ntc.group_celebrate',
  get_ready: 'ntc.group_get_ready',
  heads_up: 'ntc.group_heads_up',
  invite: 'ntc.group_invite',
};

export interface NoticePopupProps {
  step: PopupStep;
  /** The newest of the folded notices, for the folded card's message. */
  folded: Notice[];
  onDone: () => void;
  /** Its button or the card was tapped. */
  onAction: (n: Notice) => void;
  /** Whether Studio can open the button's screen; no button if not. */
  canAct: (n: Notice) => boolean;
  /** The folded card's "Open list". */
  onOpenList: () => void;
}

export default function NoticePopup({ step, folded, onDone, onAction, canAct, onOpenList }: NoticePopupProps) {
  const { t } = useI18n();
  const isFolded = step.kind === 'many';
  const item = step.kind === 'one' ? step.notice : folded[folded.length - 1];
  const urgent = step.kind === 'one' && step.notice.is_urgent;
  const [held, setHeld] = useState(false);
  const left = useRef(SHOW_MS);
  const startedAt = useRef(0);
  const touchY = useRef<number | null>(null);

  /* 6 s, paused while held; urgent never times out. */
  useEffect(() => {
    if (urgent || held) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(onDone, left.current);
    return () => {
      window.clearTimeout(timer);
      left.current = Math.max(0, left.current - (Date.now() - startedAt.current));
    };
  }, [urgent, held, onDone]);

  if (!item) return null;
  const count = step.kind === 'many' ? step.count : 1;
  const title = isFolded ? t('ntc.folded_title', { count }) : item.title;
  const message = isFolded ? t('ntc.folded_message', { title: item.title, count: count - 1 }) : item.message;
  const action = isFolded ? t('ntc.folded_action') : canAct(item) ? item.button?.label ?? null : null;
  const go = () => {
    if (isFolded) onOpenList();
    else if (canAct(item)) onAction(item);
    else return;
    onDone();
  };

  return (
    <div
      className={'npop' + (urgent ? ' npop--urgent' : '') + (held ? ' npop--held' : '')}
      role={urgent ? 'alertdialog' : 'status'}
      aria-label={t('ntc.region')}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onTouchStart={(e) => { setHeld(true); touchY.current = e.touches[0]?.clientY ?? null; }}
      onTouchEnd={(e) => {
        setHeld(false);
        const y0 = touchY.current;
        const y1 = e.changedTouches[0]?.clientY;
        touchY.current = null;
        if (y0 !== null && y1 !== undefined && y0 - y1 > 40) onDone();
      }}
      onClick={go}
    >
      <div className="npop-tile"><Tile anim={isFolded ? 'stack' : animFor(item)} count={isFolded ? count : undefined} /></div>
      <div className="npop-k">{t(isFolded ? GROUP_KEY.invite : GROUP_KEY[item.kind] ?? GROUP_KEY.heads_up)}</div>
      <button type="button" className="npop-x" aria-label={t('ntc.close')} onClick={(e) => { e.stopPropagation(); onDone(); }}>✕</button>
      <div className="npop-t">{title}</div>
      <div className="npop-m">{message}</div>
      <div className="npop-row">
        {urgent ? <span className="npop-live"><i />{t('ntc.live')}</span> : <span />}
        {action && (
          <button type="button" className="npop-btn" onClick={(e) => { e.stopPropagation(); go(); }}>{action}</button>
        )}
      </div>
      {!urgent && <span className="npop-timer" />}
    </div>
  );
}

/** On the page body, so the top bar's own layers never pin it. */
export function NoticePopupLayer(props: NoticePopupProps & { stepKey: string }) {
  const { stepKey, ...rest } = props;
  return createPortal(<NoticePopup key={stepKey} {...rest} />, document.body);
}
